import React from "react";
import { useTranslation } from "@client-app/i18n";
import type { InstallState } from "@/flows/shopifyInstallController";

type Props = {
  state: InstallState;
  workspaces: Array<{ id: string; name: string }>;
  selectedWorkspaceId: string;
  workspaceError?: boolean;
  onSelect: (id: string) => void;
  onConnect: () => void;
  onCreateWorkspace: () => void;
  onContinue: () => void;
  onAbandon: () => void;
  onRetryValidation?: () => void;
  onReviewPlan?: () => void;
  onRetryWorkspace: () => void;
  onChangeAccount: () => void;
};

export default function ShopifyInstallView(props: Props) {
  const { state } = props;
  const { t } = useTranslation();
  const canConnect =
    ["ready", "retry", "restricted"].includes(state.kind) &&
    !props.workspaceError;
  const recovery =
    state.kind === "missing" ||
    state.kind === "invalid" ||
    state.kind === "validation-error";
  return (
    <main className="min-h-screen bg-[#f7f7f5] px-6 py-16 flex items-center justify-center">
      <section
        className="w-full max-w-lg rounded-2xl border border-black/10 bg-white p-8 shadow-sm"
        aria-labelledby="shopify-install-heading"
      >
        <p className="text-sm font-semibold tracking-wide text-neutral-500 mb-5">
          BUSTLY · SHOPIFY
        </p>
        <h1
          id="shopify-install-heading"
          className="text-2xl font-semibold text-neutral-900 mb-3"
        >
          {state.kind === "success"
            ? t("website.shopifyInstall.successTitle")
            : t("website.shopifyInstall.title")}
        </h1>
        {recovery ? (
          <div role="alert" className="text-sm text-neutral-600 space-y-4">
            <p>
              {state.kind === "missing"
                ? t("website.shopifyInstall.missing")
                : state.kind === "validation-error"
                  ? t("website.shopifyInstall.validationError")
                  : t("website.shopifyInstall.invalid")}
            </p>
            {state.kind === "validation-error" && (
              <button
                type="button"
                onClick={props.onRetryValidation}
                className="underline"
              >
                {t("website.shopifyInstall.retryButton")}
              </button>
            )}
            {state.kind === "invalid" && (
              <button
                type="button"
                onClick={props.onChangeAccount}
                className="underline"
              >
                {t("common.signOut")}
              </button>
            )}
          </div>
        ) : (
          <>
            {"shop" in state && (
              <p className="text-sm text-neutral-600 mb-6 break-words">
                {state.shop}
              </p>
            )}
            {props.workspaceError ? (
              <div role="alert" className="text-sm text-neutral-600 mb-6">
                <p>{t("website.shopifyInstall.workspaceError")}</p>
                <button
                  type="button"
                  onClick={props.onRetryWorkspace}
                  className="underline mt-2"
                >
                  {t("website.shopifyInstall.retryButton")}
                </button>
              </div>
            ) : (
              <>
                {state.kind === "checking" && (
                  <p role="status" className="text-sm text-neutral-600 mb-6">
                    {t("website.shopifyInstall.checking")}
                  </p>
                )}
                {state.kind === "switching" && (
                  <p role="status" className="text-sm text-neutral-600 mb-6">
                    {t("website.shopifyInstall.switching")}
                  </p>
                )}
                {state.kind === "connecting" && (
                  <p role="status" className="text-sm text-neutral-600 mb-6">
                    {t("website.shopifyInstall.connecting")}
                  </p>
                )}
                {state.kind === "workspace-unavailable" && (
                  <div role="alert" className="text-sm text-neutral-600 mb-6">
                    <p>{t("website.shopifyInstall.workspaceUnavailable")}</p>
                    <button
                      type="button"
                      onClick={props.onRetryWorkspace}
                      className="underline mt-2"
                    >
                      {t("website.shopifyInstall.retryButton")}
                    </button>
                  </div>
                )}
                {state.kind === "workspace-required" && (
                  <p className="text-sm text-neutral-600 mb-6">
                    {t("website.shopifyInstall.workspaceRequired")}
                  </p>
                )}
                {state.kind === "retry" && (
                  <p role="alert" className="text-sm text-neutral-600 mb-6">
                    {t("website.shopifyInstall.retry")}
                  </p>
                )}
                {state.kind === "restricted" && (
                  <div role="alert" className="text-sm text-neutral-600 mb-6">
                    <p>
                      {state.restriction?.message ||
                        t("website.shopifyInstall.restricted")}
                    </p>
                    {(state.restriction?.upgradeRequired ||
                      state.restriction?.ctaAction === "renew_subscription" ||
                      state.restriction?.ctaAction ===
                        "update_payment_method") && (
                      <button
                        type="button"
                        onClick={props.onReviewPlan}
                        className="underline mt-2"
                      >
                        {t("website.shopifyInstall.reviewPlan")}
                      </button>
                    )}
                  </div>
                )}
                {state.kind === "success" && (
                  <p className="text-sm text-neutral-600 mb-6">
                    {t("website.shopifyInstall.success")}
                  </p>
                )}
                {props.workspaces.length > 0 && state.kind !== "success" && (
                  <label className="block text-sm font-medium text-neutral-700 mb-6">
                    {t("website.shopifyInstall.workspace")}
                    <select
                      value={props.selectedWorkspaceId}
                      onChange={(event) => props.onSelect(event.target.value)}
                      disabled={
                        state.kind === "connecting" || state.kind === "checking"
                      }
                      className="mt-2 block w-full rounded-lg border border-neutral-300 bg-white px-3 py-3 text-neutral-900"
                    >
                      {props.selectedWorkspaceId &&
                        !props.workspaces.some(
                          (workspace) =>
                            workspace.id === props.selectedWorkspaceId,
                        ) && (
                          <option value={props.selectedWorkspaceId} disabled>
                            {t(
                              "website.shopifyInstall.workspaceUnavailableOption",
                            )}
                          </option>
                        )}
                      {!props.selectedWorkspaceId && (
                        <option value="" disabled>
                          {t("website.shopifyInstall.chooseWorkspace")}
                        </option>
                      )}
                      {props.workspaces.map((workspace) => (
                        <option key={workspace.id} value={workspace.id}>
                          {workspace.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {state.kind === "workspace-required" ? (
                  <button
                    type="button"
                    onClick={props.onCreateWorkspace}
                    className="w-full rounded-lg bg-neutral-900 px-4 py-3 text-white font-medium"
                  >
                    {t("common.continue")}
                  </button>
                ) : state.kind === "success" ? (
                  <button
                    type="button"
                    onClick={props.onContinue}
                    className="w-full rounded-lg bg-neutral-900 px-4 py-3 text-white font-medium"
                  >
                    {t("common.continue")}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={props.onConnect}
                    disabled={!canConnect}
                    className="w-full rounded-lg bg-neutral-900 px-4 py-3 text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {state.kind === "connecting"
                      ? t("website.shopifyInstall.connectingButton")
                      : state.kind === "retry"
                        ? t("website.shopifyInstall.retryButton")
                        : t("website.shopifyInstall.connect")}
                  </button>
                )}
              </>
            )}
          </>
        )}
        {state.kind !== "success" && (
          <button
            type="button"
            onClick={props.onAbandon}
            disabled={state.kind === "connecting"}
            className="mt-5 text-sm text-neutral-500 underline disabled:opacity-40"
          >
            {t("common.cancel")}
          </button>
        )}
      </section>
    </main>
  );
}
